import SEO from '../components/seo/SEO';

export default function Terms() {
  return (
    <main className="min-h-screen px-4 py-10">
      <SEO 
        title="Terms of Service - PromptShare"
        description="Review the terms and conditions for using PromptShare, the AI prompt sharing platform."
        url="https://promptshare.pro/terms"
      />
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="space-y-2">
          <h1>PromptShare Terms and Conditions</h1>
          <p>Effective Date: 20 October 2025</p>
        </header>

        <section className="space-y-2">
          <h2>1. Introduction and Acceptance</h2>
          <p>
            PromptShare is a digital forum for disseminating prompt concepts, derivative outputs, and
            linked resources so that contributors may examine, critique, and repurpose ideation. By
            entering, browsing, uploading, linking, or otherwise interacting with this platform, you
            affirmatively accept these Terms and Conditions (the "Terms") and the ancillary documents
            incorporated by reference. Any access performed subsequent to publication of these Terms
            constitutes conclusive evidence of assent.
          </p>
        </section>

        <section className="space-y-2">
          <h2>2. Eligibility and Account Integrity</h2>
          <p>
            You warrant that you possess the legal capacity to contract within the jurisdiction in which
            you reside and that any account credentials you furnish are truthful, exhaustive, and
            continuously maintained. You must preserve the confidentiality of authentication mechanisms
            and shall be strictly liable for each act, omission, upload, or publication executed through
            your credentials, irrespective of whether you personally initiated the transaction.
          </p>
        </section>

        <section className="space-y-2">
          <h2>3. Platform Purpose and Relationship</h2>
          <p>
            PromptShare supplies access to a collaborative environment where users broadcast prompt-based
            content and companion explanations to expand collective creative intelligence. The platform
            offers hosting, indexing, and rudimentary visibility services only; it neither edits nor
            curates submissions before publication. No partnership, agency, fiduciary duty, or employment
            relationship arises between PromptShare and any user through platform participation.
          </p>
        </section>

        <section className="space-y-2">
          <h2>4. Content Submission and Responsibility</h2>
          <p>
            Every uploader remains exclusively accountable for the accuracy, legality, originality,
            licensing, and downstream consequences of their contributions, including embedded links,
            media, or AI-generated derivatives. You represent that each item of content is free of
            encumbrances, defamation, obscenity, unauthorized personal data, and any infringement upon
            intellectual property or privacy rights. PromptShare disclaims all liability for the
            distribution, reliance, or interpretation of user-generated content, and no indemnity is
            extended for losses incurred by third parties who rely upon such material.
          </p>
        </section>

        <section className="space-y-2">
          <h2>5. Usage Rights Granted to PromptShare</h2>
          <p>
            By posting or transmitting content, you grant PromptShare a perpetual, irrevocable, worldwide
            licence, sublicensable through multiple tiers, to host, store, reproduce, transmit, index,
            display, adapt, and distribute the submission within the platform functionalities. This grant
            exists solely to operate, promote, and enhance the service and does not confer ownership upon
            PromptShare. You may remove specific content at any time; however, archival copies or cached
            references maintained for operational resilience may persist.
          </p>
        </section>

        <section className="space-y-2">
          <h2>6. Community Conduct and Reporting Protocols</h2>
          <p>
            Users must honour the collaborative ethos of PromptShare and refrain from harassment,
            discrimination, disinformation, plagiarism, or any exploitation of the platform that impedes
            others from sharing ideas freely. You agree to utilise the reporting mechanism whenever you
            encounter material that appears unlawful, harmful, or contrary to these Terms. PromptShare
            remains ready to assist; reported submissions will be assessed, and remedial measures will be
            pursued expeditiously where warranted, though no specific response timeframe is guaranteed.
          </p>
        </section>

        <section className="space-y-2">
          <h2>7. Prohibited Activities</h2>
          <p>
            You shall not circumvent security controls, mine data beyond authorised interfaces, deploy
            automated harvesting scripts, misrepresent origin, conduct reverse engineering, interfere with
            infrastructure, or utilise the platform to disseminate malware, deceptive promotions, or
            unlawful solicitations. PromptShare reserves unilateral authority to restrict access whenever
            suspected misconduct compromises system integrity or community safety.
          </p>
        </section>

        <section className="space-y-2">
          <h2>8. Disclaimers</h2>
          <p>
            PromptShare delivers the service, all user-generated material, and any third-party references
            "as is" and "as available" without warranties, express or implied. No assurance is furnished
            regarding uninterrupted availability, merchantability, non-infringement, fitness for a
            particular purpose, or the accuracy of outputs derived from prompts. Use of any prompt,
            strategy, or hyperlink occurs at your exclusive risk.
          </p>
        </section>

        <section className="space-y-2">
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent allowed by governing law, PromptShare and its affiliates, officers,
            employees, and contractors shall not be liable for indirect, consequential, exemplary,
            incidental, or punitive damages, lost profits, business interruption, data loss, or reputational
            harm arising from or relating to the platform. Aggregate direct damages, if any, shall not
            exceed the greater of one hundred United States dollars (USD 100) or the amounts you paid to
            PromptShare in the twelve months preceding the claim. Content uploaders bear sole financial and
            legal responsibility for disputes emanating from their contributions.
          </p>
        </section>

        <section className="space-y-2">
          <h2>10. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless PromptShare, together with its directors,
            officers, employees, and service providers, from any claim, liability, penalty, loss, or
            expense, including reasonable attorneys&apos; fees, arising out of or connected to your content,
            your breach of these Terms, or your violation of any applicable law or third-party right.
          </p>
        </section>

        <section className="space-y-2">
          <h2>11. Service Modifications and Updates</h2>
          <p>
            PromptShare may revise, suspend, or discontinue any aspect of the platform, and may amend these
            Terms at any time, in its exclusive discretion, without prior notice. Continued access or use
            after modifications become effective constitutes irrevocable acceptance of the revised Terms.
            You are obligated to review the most current version posted on the platform at regular
            intervals.
          </p>
        </section>

        <section className="space-y-2">
          <h2>12. Termination</h2>
          <p>
            PromptShare may terminate or restrict your account and remove content without advance warning
            if it reasonably suspects a breach of these Terms or perceives a threat to the community.
            Termination shall not limit PromptShare&apos;s right to pursue additional remedies. Provisions that
            by their nature should survive termination shall continue, including Sections 4 through 10 and
            13.
          </p>
        </section>

        <section className="space-y-2">
          <h2>13. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the jurisdiction where PromptShare maintains its
            principal place of business, without regard to conflict-of-law principles. Any dispute shall be
            submitted to the exclusive jurisdiction of the courts situated within that jurisdiction. You
            irrevocably waive objections to venue and consent to personal jurisdiction in those courts.
          </p>
        </section>

        <section className="space-y-2">
          <h2>14. Contact</h2>
          <p>
            Questions regarding these Terms may be directed to the PromptShare support team via the contact
            channel identified within the platform. PromptShare endeavours to respond promptly, yet no
            guaranteed response timeline is assumed.
          </p>
        </section>

        <section className="space-y-2">
          <h2>15. Intellectual Property Notices; Takedown; Repeat Infringers</h2>
          <p>
            PromptShare respects intellectual property and expects users to do the same. If you believe that
            content available on the platform infringes your rights, you may submit a notice containing
            sufficient detail to permit identification of the material and the basis of the claim through the
            platform&rsquo;s designated contact facility. Upon receipt of a compliant notice, PromptShare may
            disable access to the material, notify the uploader, and, where appropriate, restore access upon
            receiving a substantiated counter-notice. PromptShare maintains a policy for termination, in
            appropriate circumstances, of users who are deemed repeat infringers.
          </p>
        </section>

        <section className="space-y-2">
          <h2>16. Limited Licence to Other Users</h2>
          <p>
            To fulfil the collaborative purpose of PromptShare, you grant to each other user of the platform a
            non-exclusive, worldwide licence to access, view, bookmark, reference, and generate outputs from
            your posted prompts for personal or internal purposes via the platform interface, subject to any
            rights you expressly reserve in your submission and to applicable law. This clause does not grant
            any user authority to claim authorship, remove notices, or redistribute your content outside the
            platform beyond what is technically enabled by built-in sharing features.
          </p>
        </section>
      </div>
    </main>
  );
}
